import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  BooleanField,
  ReferenceField,
  FunctionField,
  useRecordContext,
} from 'react-admin';
import {
  Card,
  Typography,
  Box,
  Divider,
  Grid,
  Chip,
  Paper,
} from '@mui/material';
import { CheckCircle, Cancel, Storage, ViewColumn } from '@mui/icons-material';
import { TaskDataSourceType, TaskFunctionType } from '@/api/gen/model';
import { useGetApiVariablesId, useGetApiCsvColunmsId } from '@/api/gen/api';
import { formatTimeSpanExpression } from '@/components/formatTimeSpane';

// Componente para mostrar el tipo de fuente de datos con estilo
const DataSourceTypeDisplay = () => {
  const record = useRecordContext();
  if (!record?.dataSourceType) return <span>-</span>;

  const typeLabels: Record<string, { label: string; icon: any }> = {
    VARIABLE: { label: 'Variable', icon: <Storage /> },
    CSV_COLUMN: {
      label: 'Columna CSV',
      icon: <ViewColumn />,
    },
  };

  const type = typeLabels[record.dataSourceType] || {
    label: record.dataSourceType,
    icon: null,
  };

  return (
    <Chip
      label={type.label}
      icon={type.icon}
      color='primary'
      variant='outlined'
      sx={{
        fontSize: '0.9rem',
        padding: '8px 4px',
      }}
    />
  );
};

// Componente para mostrar el tipo de cálculo
const CalculationTypeDisplay = () => {
  const record = useRecordContext();
  if (!record?.instantCalculationType) return <span>No especificado</span>;

  const calcLabels: Record<string, string> = {
    [TaskFunctionType.AVG]: 'Promedio',
    [TaskFunctionType.SUM]: 'Suma',
    [TaskFunctionType.MAX]: 'Máximo',
    [TaskFunctionType.MIN]: 'Mínimo',
    [TaskFunctionType.TOTALIZED]: 'Totalizado',
    [TaskFunctionType.CUMULATIVE]: 'Acumulado',
  };

  return (
    <Chip
      label={
        calcLabels[record.instantCalculationType] ||
        record.instantCalculationType
      }
      color='primary'
      variant='outlined'
      sx={{ fontSize: '0.9rem' }}
    />
  );
};

// Componente para mostrar instantEnabled con iconos
const InstantEnabledDisplay = () => {
  const record = useRecordContext();
  const isEnabled = record?.instantEnabled;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {isEnabled ? (
        <>
          <CheckCircle color='success' />
          <Typography color='success.main' fontWeight={500}>
            Habilitado
          </Typography>
        </>
      ) : (
        <>
          <Cancel color='error' />
          <Typography color='error.main' fontWeight={500}>
            Deshabilitado
          </Typography>
        </>
      )}
    </Box>
  );
};

// Componente para mostrar los detalles del dataSource según el tipo
const DataSourceDetails = () => {
  const record = useRecordContext();
  const dataSource = record?.dataSource;

  // Hook para obtener variable si es de tipo VARIABLE
  const { data: variableData } = useGetApiVariablesId(
    dataSource?.variableId || 0,
    {
      query: {
        enabled:
          dataSource?.dataSourceType === 'VARIABLE' && !!dataSource?.variableId,
      },
    }
  );

  // Hook para obtener la columna CSV principal
  // El backend puede devolver columnId o csvColumnId
  const { data: columnData } = useGetApiCsvColunmsId(
    dataSource?.csvColumnId || dataSource?.columnId || 0,
    {
      query: {
        enabled:
          dataSource?.dataSourceType === 'CSV_COLUMN' &&
          !!(dataSource?.csvColumnId || dataSource?.columnId),
      },
    }
  );

  if (!dataSource) return <Typography>No especificado</Typography>;

  if (dataSource.dataSourceType === 'VARIABLE') {
    return (
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant='caption' color='text.secondary'>
                Variable SCADA
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant='body1' fontWeight={500}>
                  {(variableData as any)?.variableName ||
                    `ID: ${dataSource.variableId}`}
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (dataSource.dataSourceType === 'CSV_COLUMN') {
    return (
      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <Typography variant='caption' color='text.secondary'>
                Columna CSV
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant='body1' fontWeight={500}>
                  {columnData?.header ||
                    `ID: ${dataSource.csvColumnId || dataSource.columnId}`}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {(dataSource.csvFilters || dataSource.filters) &&
            (dataSource.csvFilters?.length > 0 ||
              dataSource.filters?.length > 0) && (
              <Grid item xs={12}>
                <Typography variant='subtitle2' gutterBottom sx={{ mt: 2 }}>
                  Filtros CSV
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(dataSource.csvFilters || dataSource.filters).map(
                    (filter: any, index: number) => (
                      <FilterColumnDisplay
                        key={filter.id || index}
                        filter={filter}
                      />
                    )
                  )}
                </Box>
              </Grid>
            )}
        </Grid>
      </Box>
    );
  }

  return <Typography>Tipo de fuente desconocido</Typography>;
};

// Componente separado para mostrar cada filtro con su columna
const FilterColumnDisplay = ({ filter }: { filter: any }) => {
  // El backend puede usar csvColumnId o csvColunmId (typo en el backend)
  const { data: filterColumnData } = useGetApiCsvColunmsId(
    filter.csvColumnId || filter.csvColunmId || 0,
    {
      query: {
        enabled: !!(filter.csvColumnId || filter.csvColunmId),
      },
    }
  );

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Typography variant='caption' color='text.secondary'>
            Columna
          </Typography>
          <Typography variant='body2' fontWeight={500}>
            {filterColumnData?.header ||
              filter.csvColumnId ||
              filter.csvColunmId}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant='caption' color='text.secondary'>
            Operador
          </Typography>
          <Typography variant='body2' fontWeight={500}>
            {filter.operator || filter.csvFilterOperator}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant='caption' color='text.secondary'>
            Valor
          </Typography>
          <Typography variant='body2' fontWeight={500}>
            {filter.value}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default function SubscriptionShow() {
  return (
    <Show>
      <SimpleShowLayout>
        <Card sx={{ p: 3, mb: 2 }}>
          <Typography
            variant='h6'
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Storage color='primary' />
            Información General
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant='caption' color='text.secondary'>
                  ID
                </Typography>
                <TextField
                  source='id'
                  sx={{ display: 'block', fontWeight: 500 }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant='caption' color='text.secondary'>
                  Planta
                </Typography>
                <ReferenceField source='plantId' reference='plants' link='show'>
                  <TextField
                    source='plantName'
                    sx={{ display: 'block', fontWeight: 500 }}
                  />
                </ReferenceField>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant='caption' color='text.secondary'>
                  Referencia SCADA
                </Typography>
                <TextField
                  source='scadaReferenceName'
                  sx={{ display: 'block', fontWeight: 500, fontSize: '1.1rem' }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant='caption' color='text.secondary'>
                  Tipo de Fuente de Datos
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <DataSourceTypeDisplay />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant='caption' color='text.secondary'>
                  ID de Fuente de Datos
                </Typography>
                <TextField
                  source='dataSourceId'
                  sx={{ display: 'block', fontWeight: 500 }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant='caption' color='text.secondary'>
                  Cambio de Unidad
                </Typography>
                <TextField
                  source='unitChange'
                  sx={{ display: 'block', fontWeight: 500 }}
                  emptyText='No especificado'
                />
              </Box>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ p: 3, mb: 2 }}>
          <Typography
            variant='h6'
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            ⚡ Configuración Instantánea
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant='caption' color='text.secondary'>
                  Estado
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <InstantEnabledDisplay />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant='caption' color='text.secondary'>
                  Tipo de Cálculo
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <CalculationTypeDisplay />
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ mb: 2 }}>
                <Typography variant='caption' color='text.secondary'>
                  Expresión Instantánea
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mt: 1,
                    fontFamily: 'monospace',
                    fontSize: '1rem',
                    fontWeight: 500,
                  }}
                >
                  <FunctionField
                    source='instantExpression'
                    render={(record: any) =>
                      formatTimeSpanExpression(record.instantExpression) ||
                      'No especificada'
                    }
                  />
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ p: 3, mb: 2 }}>
          <Typography
            variant='h6'
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <Storage color='primary' />
            Fuente de Datos
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <DataSourceDetails />
        </Card>
      </SimpleShowLayout>
    </Show>
  );
}
